import { SceneGraph } from '@/core/scene/sceneGraph';
import { createEventBus, type SpatialEventBus } from '@/core/events/eventBus';
import { FrameScheduler } from '@/core/rendering/frameScheduler';
import { CameraAnimator } from '@/features/camera/cameraAnimator';
import { useSettingsStore } from '@/store/settingsStore';
import { type ApplicationRegistry, createDefaultRegistry } from '@/applications';
import { useWorkspaceStore, selectActiveObjects } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useCameraStore } from '@/store/cameraStore';
import { useWindowStore } from '@/store/windowStore';
import type { ConnectionId, ObjectId } from '@/types';

/**
 * Non-React runtime: the scene graph, event bus, frame scheduler and application
 * registry. Created once, wired to the stores, and provided to React via context.
 */
export interface Engine {
  readonly scene: SceneGraph;
  readonly events: SpatialEventBus;
  readonly frames: FrameScheduler;
  readonly applications: ApplicationRegistry;
  /** Smooth camera transitions; cancelled by direct user input. */
  readonly animator: CameraAnimator;
  /** Syncs the scene graph with the store if it is behind; safe to call from selectors. */
  readonly ensureSynced: () => void;
  /** Real diagnostics: duration of the last scene sync (ms) and how many objects were synced. */
  readonly diagnostics: () => {
    readonly lastSyncMs: number;
    readonly lastSyncSize: number;
    readonly syncCount: number;
  };
  /** Wires store subscriptions. Idempotent; safe under React StrictMode double-invocation. */
  readonly start: () => void;
  /** Tears down subscriptions and pending frames. The engine can be started again. */
  readonly stop: () => void;
}

export const createEngine = (): Engine => {
  const scene = new SceneGraph();
  const events = createEventBus();
  const frames = new FrameScheduler();
  const applications = createDefaultRegistry();
  const animator = new CameraAnimator(frames);

  let disposers: (() => void)[] = [];
  let synced: { objects: unknown; workspaceId: unknown } = { objects: null, workspaceId: null };
  let diag = { lastSyncMs: 0, lastSyncSize: 0, syncCount: 0 };

  /**
   * Brings the scene graph up to date with the store. Idempotent and cheap when nothing
   * changed, so React selectors that read scene bounds can call it first: component
   * subscriptions are registered before the engine's (child effects run before the
   * parent's), so without this they would observe the previous document's geometry.
   */
  const syncScene = () => {
    const state = useWorkspaceStore.getState();
    const objects = selectActiveObjects(state) ?? {};
    if (synced.objects === objects && synced.workspaceId === state.activeWorkspaceId) return;
    synced = { objects, workspaceId: state.activeWorkspaceId };
    const t0 = performance.now();
    scene.sync(objects);
    diag = { lastSyncMs: performance.now() - t0, lastSyncSize: scene.size, syncCount: diag.syncCount + 1 };
    const ids = new Set(Object.keys(objects) as ObjectId[]);
    const connectionIds = new Set(
      Object.keys(state.workspaces[state.activeWorkspaceId]?.connections ?? {}) as ConnectionId[],
    );
    useSelectionStore.getState().prune(ids, connectionIds);
    useWindowStore.getState().prune(ids);
  };

  const start = () => {
    if (disposers.length) return;
    synced = { objects: null, workspaceId: null };
    syncScene();
    animator.setReduceMotion(useSettingsStore.getState().reduceMotion);
    disposers = [
      useSettingsStore.subscribe((s) => {
        animator.setReduceMotion(s.reduceMotion);
      }),
      useWorkspaceStore.subscribe((s, prev) => {
        if (s.activeWorkspaceId !== prev.activeWorkspaceId) {
          events.emit({ type: 'workspace:switched', workspaceId: s.activeWorkspaceId });
          useSelectionStore.getState().clear();
        }
        if (s.revision !== prev.revision || s.activeWorkspaceId !== prev.activeWorkspaceId) syncScene();
      }),
      useSelectionStore.subscribe((s, prev) => {
        if (s.selection !== prev.selection) events.emit({ type: 'selection:changed', selection: s.selection });
      }),
      useCameraStore.subscribe((s, prev) => {
        if (s.camera !== prev.camera) events.emit({ type: 'camera:changed', camera: s.camera });
      }),
    ];
  };

  const stop = () => {
    for (const dispose of disposers) dispose();
    disposers = [];
    animator.cancel();
    frames.cancel();
  };

  return {
    scene,
    events,
    frames,
    applications,
    animator,
    ensureSynced: syncScene,
    diagnostics: () => diag,
    start,
    stop,
  };
};
